import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { isPlaceholder, readPresentation } from "@/office/powerpoint/presentation";
import { readParagraphLevels } from "@/office/powerpoint/text";
import { nichtPruefbar } from "../schnittstelle";

export const lektionEbenen: Lesson = {
  id: "ebenen",
  title: "Gliederungsebenen",
  summary: "Untergeordnete Punkte einrücken – mit der Tabulatortaste, nicht mit Leerzeichen.",
  durationMinutes: 9,
  learningGoals: [
    "Stichpunkte über die Tabulatortaste ein- und ausrücken",
    "Verstehen, woher Schriftgröße und Aufzählungszeichen einer Ebene kommen",
    "Erkennen, wann eine dritte Ebene ein Zeichen für eine zu volle Folie ist",
  ],
  steps: [
    {
      id: "einruecken",
      title: "Ein- und ausrücken",
      explanation: [
        "Ein Inhaltsplatzhalter kennt mehrere Gliederungsebenen. Die Tabulatortaste am " +
          "Anfang einer Zeile rückt den Stichpunkt eine Ebene tiefer, Umschalt + Tab " +
          "wieder eine Ebene höher. Das funktioniert in der Folie genauso wie in der " +
          "Gliederungsansicht.",
        "Wichtig ist, dass es sich dabei nicht um einen Einzug im Sinne von Abstand " +
          "handelt, sondern um eine Ebene mit eigener Bedeutung. Jede Ebene hat im Layout " +
          "ihre eigene Schriftgröße, ihr eigenes Aufzählungszeichen und ihren eigenen " +
          "Abstand – festgelegt im Folienmaster, für die ganze Präsentation einheitlich.",
        "Der verbreitete Ersatz ist, den Punkt mit Leerzeichen oder einem eingetippten " +
          "Bindestrich nach rechts zu schieben. Das sieht auf den ersten Blick gleich " +
          "aus, hat aber keine der Eigenschaften: keine eigene Schriftgröße, kein " +
          "Aufzählungszeichen, keine Ebene in der Gliederungsansicht und nichts, was ein " +
          "Screenreader als Unterordnung erkennen könnte. Es ist dasselbe Muster wie die " +
          "getippte Liste im Word-Kurs.",
      ],
      keyPoints: [
        "Tab rückt eine Ebene tiefer, Umschalt + Tab eine Ebene höher.",
        "Alt + Umschalt + → und ← tun dasselbe, auch mitten im Text.",
        "Leerzeichen und getippte Striche erzeugen keine Ebene.",
      ],
      task: {
        instruction:
          "Baue in einem Inhaltsplatzhalter mindestens einen untergeordneten Stichpunkt " +
          "über die Tabulatortaste ein – also eine zweite Gliederungsebene.",
        hint: "Cursor an den Anfang der Zeile setzen und Tab drücken.",
        check: async () => {
          const snapshot = await readPresentation();
          const ebenen = await readParagraphLevels();

          if (ebenen === null) {
            return nichtPruefbar(
              "Die Gliederungsebene eines Absatzes",
              "1.10",
              "Kontrolliere selbst in der Gliederungsansicht, ob der untergeordnete Punkt dort eingerückt erscheint.",
            );
          }
          if (!snapshot.features.shapeDetails) {
            return nichtPruefbar(
              "Die Art einer Form (Platzhalter oder Textfeld)",
              "1.4",
              "Kontrolliere in der Gliederungsansicht, ob die zweite Ebene erscheint.",
            );
          }

          const platzhalterIds = new Set(
            snapshot.slides
              .flatMap((folie) => folie.shapes)
              .filter(isPlaceholder)
              .map((form) => form.id),
          );
          const inPlatzhaltern = ebenen.filter((eintrag) => platzhalterIds.has(eintrag.shapeId));
          const zweiteEbene = inPlatzhaltern.filter(
            (eintrag) => eintrag.level !== null && eintrag.level >= 2,
          );

          if (zweiteEbene.length > 0) {
            return pass(
              `Auf Folie ${zweiteEbene[0].slideIndex + 1} steht „${zweiteEbene[0].text.slice(0, 45)}“ auf Ebene ${zweiteEbene[0].level} – die Unterordnung ist echt.`,
            );
          }

          const verdaechtig = inPlatzhaltern.find((eintrag) =>
            /^\s*[-–•*]\s+/.test(eintrag.text),
          );

          return fail(
            "In den Platzhaltern gibt es bisher nur Stichpunkte auf der obersten Ebene.",
            verdaechtig
              ? `Auf Folie ${verdaechtig.slideIndex + 1} beginnt ein Punkt mit einem getippten Strich („${verdaechtig.text.slice(0, 40)}“). Lösch den Strich und drück stattdessen am Zeilenanfang die Tabulatortaste.`
              : "Cursor an den Anfang eines untergeordneten Stichpunkts setzen und Tab drücken.",
          );
        },
      },
    },
    {
      id: "wie-viele-ebenen",
      title: "Wie viele Ebenen sinnvoll sind",
      explanation: [
        "PowerPoint bietet fünf Gliederungsebenen an. Sinnvoll sind zwei, in " +
          "Ausnahmefällen drei.",
        "Der Grund ist wieder die Wahrnehmung: Eine Ebene erkennt man am Einzug, zwei " +
          "unterscheidet man mühelos. Ab der dritten Ebene muss das Publikum die " +
          "Einrückungstiefe vergleichen, um die Struktur zu verstehen – und das im " +
          "Vorbeigehen, während jemand spricht. Das funktioniert nicht.",
        "Wenn eine Folie eine vierte Ebene braucht, ist nicht die Gliederung das Problem, " +
          "sondern der Umfang. Solche Inhalte gehören auf mehrere Folien, oder – wenn es " +
          "wirklich um eine Hierarchie geht – in eine SmartArt-Grafik oder ein Diagramm, " +
          "das die Struktur zeigt, statt sie durch Einrückung anzudeuten.",
      ],
      keyPoints: [
        "Zwei Ebenen sind der Normalfall, drei die Ausnahme.",
        "Ab Ebene vier: Folie teilen oder eine Grafik verwenden.",
        "Die Aufzählungszeichen jeder Ebene kommen aus dem Folienmaster.",
      ],
      task: {
        instruction:
          "Prüfe deine Folien: In keinem Platzhalter sollte die Gliederung tiefer als " +
          "drei Ebenen gehen.",
        hint: "Umschalt + Tab holt einen zu tief eingerückten Punkt wieder nach oben.",
        check: async () => {
          const ebenen = await readParagraphLevels();

          if (ebenen === null) {
            return nichtPruefbar(
              "Die Gliederungsebene eines Absatzes",
              "1.10",
              "Sieh in der Gliederungsansicht nach, wie tief deine Stichpunkte eingerückt sind.",
            );
          }

          const zuTief = ebenen.filter((eintrag) => eintrag.level !== null && eintrag.level > 3);
          if (zuTief.length === 0) {
            const maximum = ebenen.reduce(
              (hoechste, eintrag) => Math.max(hoechste, eintrag.level ?? 0),
              0,
            );
            return pass(
              maximum === 0
                ? "Es gibt noch keine mehrstufige Gliederung – damit ist auch nichts zu tief."
                : `Die tiefste Gliederungsebene ist Ebene ${maximum} – das bleibt auf einen Blick lesbar.`,
            );
          }

          return fail(
            `Auf Folie ${zuTief[0].slideIndex + 1} steht „${zuTief[0].text.slice(0, 45)}“ auf Ebene ${zuTief[0].level}.` +
              (zuTief.length > 1 ? ` Insgesamt sind ${zuTief.length} Punkte zu tief eingerückt.` : ""),
            "Mit Umschalt + Tab eine Ebene höher holen – oder die Folie teilen, wenn dann zu viele Punkte auf einer Ebene stehen.",
          );
        },
      },
    },
  ],
};
